<template>
  <div id="app">
    <template v-if="isLoggedIn && $route.path !== '/login'">
      <el-container class="app-container" :class="appStateClasses">
        <!-- 顶部导航栏 -->
        <el-header class="app-header">
          <div class="header-left">
            <button
              v-if="isLayoutVertical"
              type="button"
              class="header-action-btn menu-toggle-btn"
              :title="isCollapsed ? '展开菜单' : '收起菜单'"
              :aria-label="isCollapsed ? '展开菜单' : '收起菜单'"
              @click="toggleMenu"
            >
              <svg class="hamburger-icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M3 4h18v2H3V4z"/>
                <path d="M3 11h18v2H3v-2z"/>
                <path d="M3 18h10v2H3v-2z"/>
              </svg>
            </button>

            <template v-if="isLayoutHorizontal">
              <div class="logo-wrapper">
                <i class="el-icon-aim"></i>
              </div>
              <div class="header-nav-tabs">
                <button
                  v-for="item in visibleMenuItems"
                  :key="item.index"
                  type="button"
                  class="nav-tab-item"
                  :class="{ active: isMenuItemActive(item) }"
                  @click="navigateMenuItem(item)"
                >
                  <i :class="item.icon"></i>
                  <span class="tab-label">{{ item.title }}</span>
                </button>
              </div>
            </template>
          </div>
          <div class="header-right">
            <button
              ref="modeToggleBtn"
              type="button"
              class="header-action-btn mode-switch-btn"
              :class="{ 'is-dark': themeMode === 'dark', 'is-animating': themeTransitionActive }"
              :title="themeMode === 'dark' ? '切换浅色模式' : '切换深色模式'"
              :aria-label="themeMode === 'dark' ? '切换浅色模式' : '切换深色模式'"
              @click="toggleThemeModeWithAnimation"
            >
              <i :class="themeMode === 'dark' ? 'el-icon-moon' : 'el-icon-sunny'"></i>
            </button>

            <el-popover
              v-model="themePopoverVisible"
              placement="bottom-end"
              trigger="click"
              popper-class="theme-popover"
              width="320"
            >
              <div class="theme-panel">
                <div class="theme-panel__header">
                  <div>
                    <div class="theme-panel__title">界面主题</div>
                    <div class="theme-panel__desc">调整主色和整体氛围</div>
                  </div>
                  <div
                    class="theme-preview-chip"
                    :style="{ background: currentTheme.primary }"
                  ></div>
                </div>

                <div class="theme-section">
                  <div class="theme-section__label">推荐配色</div>
                  <div class="theme-preset-list">
                    <button
                      v-for="theme in themePresets"
                      :key="theme.id"
                      type="button"
                      class="theme-preset"
                      :class="{ active: activeThemeId === theme.id }"
                      @click="applyPresetTheme(theme)"
                    >
                      <span class="theme-preset__swatch" :style="{ background: theme.primary }"></span>
                      <span class="theme-preset__name">{{ theme.name }}</span>
                    </button>
                  </div>
                </div>

                <div class="theme-section">
                  <div class="theme-section__label">自定义主色</div>
                  <div class="theme-color-row">
                    <input
                      v-model="customThemeColor"
                      class="theme-color-input"
                      type="color"
                      @input="applyCustomThemeColor"
                    >
                    <div class="theme-color-meta">
                      <span class="theme-color-value">{{ customThemeColor.toUpperCase() }}</span>
                      <el-button type="text" class="theme-reset-btn" @click="resetTheme">
                        恢复默认
                      </el-button>
                    </div>
                  </div>
                </div>

                <div class="theme-section">
                  <div class="theme-section__label">界面密度</div>
                  <div class="theme-option-list density-list">
                    <button
                      v-for="item in densityOptions"
                      :key="item.value"
                      type="button"
                      class="theme-option density-option"
                      :class="{ active: themeDensity === item.value }"
                      @click="setThemeDensity(item.value)"
                    >
                      {{ item.label }}
                    </button>
                  </div>
                </div>

                <div v-if="!isMobile" class="theme-section">
                  <div class="theme-section__label">界面布局</div>
                  <div class="theme-option-list layout-list">
                    <button
                      v-for="item in layoutOptions"
                      :key="item.value"
                      type="button"
                      class="theme-option layout-option"
                      :class="{ active: layoutMode === item.value }"
                      @click="setLayoutMode(item.value)"
                    >
                      {{ item.label }}
                    </button>
                  </div>
                </div>
              </div>
              <el-button slot="reference" type="text" class="header-action-btn theme-trigger">
                <span class="theme-trigger__dot" :style="{ background: currentTheme.primary }"></span>
                <i class="el-icon-brush"></i>
              </el-button>
            </el-popover>

            <el-dropdown trigger="click" @command="handleUserCommand">
              <button type="button" class="account-trigger">
                <span class="user-avatar account-trigger__avatar" :class="roleBadgeClass">
                  <i :class="roleIcon"></i>
                </span>
                <span class="account-trigger__meta">
                  <span class="account-trigger__role">{{ roleShortLabel }}</span>
                  <i class="el-icon-arrow-down account-arrow"></i>
                </span>
                <i class="el-icon-arrow-down account-arrow"></i>
              </button>
              <el-dropdown-menu slot="dropdown" class="account-dropdown-menu">
                <el-dropdown-item disabled class="account-summary-item">
                  <div class="account-summary">
                    <div class="account-summary__avatar" :class="roleBadgeClass">
                      <i :class="roleIcon"></i>
                    </div>
                    <div class="account-summary__content">
                      <div class="account-summary__name">{{ username }}</div>
                      <div class="account-summary__role">{{ roleLabel }}</div>
                    </div>
                  </div>
                </el-dropdown-item>
                <el-dropdown-item command="logout" divided class="logout-menu-item">
                  <i class="el-icon-switch-button"></i>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </el-header>

        <el-container class="main-container">
          <!-- 侧边导航 -->
          <el-aside
            width="auto"
            class="app-aside"
            :class="{
              collapsed: isLayoutVertical && isCollapsed,
              'horizontal-aside': isLayoutHorizontal,
            }"
          >
            <div class="aside-brand" :class="{ collapsed: isLayoutVertical && isCollapsed }">
              <div class="aside-brand__icon">
                <i :class="isLayoutHorizontal && activeHorizontalMenuItem ? activeHorizontalMenuItem.icon : 'el-icon-aim'"></i>
              </div>
              <div v-show="isLayoutHorizontal || !isCollapsed" class="aside-brand__text">
                <div class="aside-brand__title">
                  {{ isLayoutHorizontal && activeHorizontalMenuItem ? activeHorizontalMenuItem.title : '货号匹配系统' }}
                </div>
                <div class="aside-brand__subtitle">
                  {{ isLayoutHorizontal ? '功能导航' : '智能匹配工作台' }}
                </div>
              </div>
            </div>
            <el-menu
              :default-active="$route.path"
              :collapse="isLayoutVertical && isCollapsed"
              :collapse-transition="false"
              router
              class="aside-menu"
            >
              <template v-for="item in sidebarMenuItems">
                <el-submenu
                  v-if="item.children && item.children.length"
                  :key="`group-${item.index}`"
                  :index="`group-${item.index}`"
                >
                  <template slot="title">
                    <i :class="item.icon"></i>
                    <span>{{ item.title }}</span>
                  </template>
                  <el-menu-item
                    v-for="child in item.children"
                    :key="child.index"
                    :index="child.index"
                    class="menu-item-animate"
                  >
                    <i :class="child.icon"></i>
                    <span slot="title">{{ child.title }}</span>
                  </el-menu-item>
                </el-submenu>
                <el-menu-item
                  v-else
                  :key="item.index"
                  :index="item.index"
                  class="menu-item-animate"
                >
                  <i :class="item.icon"></i>
                  <span slot="title">{{ item.title }}</span>
                </el-menu-item>
              </template>
            </el-menu>
            <div
              v-if="isLayoutHorizontal && activeHorizontalMenuItem && !sidebarMenuItems.length"
              class="aside-empty-state"
            >
              暂无子菜单
            </div>
          </el-aside>

          <!-- 移动端侧边栏遮罩 -->
          <div
            v-if="isLayoutVertical && isMobile && !isCollapsed"
            class="sidebar-mask"
            @click="isCollapsed = true"
          ></div>

          <!-- 主内容区 -->
          <el-main class="app-main">
            <transition name="fade-slide" mode="out-in">
              <router-view :key="$route.path" />
            </transition>
          </el-main>
        </el-container>
      </el-container>
    </template>
    <template v-else>
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

const STORAGE_THEME_KEY = 'app-theme-preference'
const STORAGE_THEME_MODE_KEY = 'app-theme-mode'
const STORAGE_THEME_DENSITY_KEY = 'app-theme-density'
const STORAGE_LAYOUT_MODE_KEY = 'app-layout-mode'

export default {
  name: 'App',
  data() {
    return {
      isCollapsed: false,
      isMobile: false,
      themePopoverVisible: false,
      themeTransitionActive: false,
      themeTransitionTimer: null,
      activeThemeId: 'default',
      customThemeColor: '#5b8def',
      themeMode: 'light',
      themeDensity: 'comfortable',
      layoutMode: 'horizontal',
      headerMenuItems: [
        { index: '/task-work', title: '开始任务', icon: 'el-icon-video-play', roles: ['worker'] },
        { index: '/review-work', title: '开始审核', icon: 'el-icon-view', roles: ['admin', 'superadmin'] },
        { index: '/tasks', title: '任务列表', icon: 'el-icon-s-order', roles: ['all'] },
        { index: '/bills', title: '我的账单', icon: 'el-icon-wallet', roles: ['all'], exclude: ['reviewer'] },
        { index: '/users', title: '用户管理', icon: 'el-icon-user', roles: ['superadmin'] },
        {
          index: '/import',
          title: '文件处理',
          icon: 'el-icon-upload2',
          roles: ['superadmin'],
          children: [
            { index: '/import/sku', title: 'SKU导入', icon: 'el-icon-document-add', roles: ['superadmin'] },
            { index: '/import/pdf', title: 'CN-US任务', icon: 'el-icon-document', roles: ['superadmin'] },
            { index: '/import/price-check', title: '比价任务', icon: 'el-icon-money', roles: ['superadmin'] },
          ],
        },
      ],
      layoutOptions: [
        { label: '水平', value: 'horizontal' },
        { label: '垂直', value: 'vertical' },
      ],
      themePresets: [
        {
          id: 'default',
          name: '云海蓝',
          primary: '#5b8def',
          pageBg: '#f5f7fa',
        },
        {
          id: 'emerald',
          name: '松石绿',
          primary: '#18b38a',
          pageBg: '#f3faf8',
        },
        {
          id: 'violet',
          name: '星夜紫',
          primary: '#7a5af8',
          pageBg: '#f6f3ff',
        },
        {
          id: 'sunset',
          name: '晚霞橙',
          primary: '#f08a24',
          pageBg: '#fff7f1',
        },
        {
          id: 'aurora',
          name: '极光青',
          primary: '#0ea5a3',
          pageBg: '#f1fbfb',
        },
        {
          id: 'rose',
          name: '玫瑰红',
          primary: '#e4587c',
          pageBg: '#fff4f7',
        },
      ],
      densityOptions: [
        { label: '紧凑', value: 'compact' },
        { label: '舒适', value: 'comfortable' },
        { label: '宽松', value: 'spacious' },
      ],
      currentTheme: {
        id: 'default',
        primary: '#5b8def',
        pageBg: '#f5f7fa',
      },
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'isAdmin', 'isSuperadmin', 'isReviewer', 'isWorker', 'username', 'role']),
    roleLabel() {
      const mapping = {
        superadmin: '超级管理员',
        reviewer: '审核员',
        worker: '匹配员',
      }
      return mapping[this.role] || '系统用户'
    },
    roleIcon() {
      const mapping = {
        superadmin: 'el-icon-medal',
        reviewer: 'el-icon-view',
        worker: 'el-icon-s-custom',
      }
      return mapping[this.role] || 'el-icon-user-solid'
    },
    roleBadgeClass() {
      return `role-${this.role || 'default'}`
    },
    roleShortLabel() {
      const mapping = {
        superadmin: '超管',
        reviewer: '审核',
        worker: '匹配',
      }
      return mapping[this.role] || '账号'
    },
    appStateClasses() {
      return {
        'theme-dark': this.themeMode === 'dark',
        'theme-light': this.themeMode === 'light',
        'density-compact': this.themeDensity === 'compact',
        'density-comfortable': this.themeDensity === 'comfortable',
        'density-spacious': this.themeDensity === 'spacious',
        'layout-horizontal': this.layoutMode === 'horizontal',
        'layout-vertical': this.layoutMode === 'vertical',
        'menu-collapsed': this.isCollapsed,
      }
    },
    visibleMenuItems() {
      return this.headerMenuItems
        .map((item) => {
          const children = Array.isArray(item.children)
            ? item.children.filter((child) => this.hasMenuPermission(child))
            : null

          if (children && children.length > 0) {
            return { ...item, children }
          }
          if (children) {
            return null
          }
          return this.hasMenuPermission(item) ? item : null
        })
        .filter((item) => !!item)
    },
    activeHorizontalMenuItem() {
      return this.visibleMenuItems.find((item) => this.isMenuItemActive(item)) || null
    },
    sidebarMenuItems() {
      if (this.isLayoutVertical) {
        return this.visibleMenuItems
      }
      return this.activeHorizontalMenuItem && this.activeHorizontalMenuItem.children
        ? this.activeHorizontalMenuItem.children
        : []
    },
    isLayoutHorizontal() {
      return this.layoutMode === 'horizontal'
    },
    isLayoutVertical() {
      return this.layoutMode === 'vertical'
    },
  },
  created() {
    this.checkMobile()
    this.initializeTheme()
    this.initializeThemeMode()
    this.initializeThemeDensity()
    this.initializeLayoutMode()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy() {
    if (this.themeTransitionTimer) {
      clearTimeout(this.themeTransitionTimer)
      this.themeTransitionTimer = null
    }
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    ...mapActions(['logout']),
    hasMenuPermission(item) {
      if (item.roles.includes('all')) {
        return !(item.exclude && item.exclude.includes(this.role))
      }
      if (item.roles.includes('worker') && this.isWorker) return true
      if (item.roles.includes('admin') && this.isAdmin) return true
      if (item.roles.includes('superadmin') && this.isSuperadmin) return true
      if (item.roles.includes('reviewer') && this.isReviewer) return true
      return false
    },
    isMenuItemActive(item) {
      if (item.children && item.children.length) {
        return item.children.some((child) => (
          this.$route.path === child.index || this.$route.path.startsWith(`${child.index}/`)
        ))
      }
      return this.$route.path === item.index || this.$route.path.startsWith(`${item.index}/`)
    },
    navigateMenuItem(item) {
      if (item.children && item.children.length && this.isMenuItemActive(item)) {
        return
      }
      const target = item.children && item.children.length
        ? item.children[0].index
        : item.index
      this.navigateToMenuPath(target)
    },
    navigateToMenuPath(target) {
      if (target && target !== this.$route.path) {
        this.$router.push(target)
      }
    },
    toggleThemeModeWithAnimation() {
      const nextMode = this.themeMode === 'dark' ? 'light' : 'dark'
      const root = document.documentElement
      const appEl = this.$el && this.$el.querySelector('.app-container')
      const toggleBtn = this.$refs.modeToggleBtn

      if (!appEl || !toggleBtn) {
        this.setThemeMode(nextMode)
        return
      }

      const rect = toggleBtn.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const maxRadius = Math.hypot(
        Math.max(centerX, window.innerWidth - centerX),
        Math.max(centerY, window.innerHeight - centerY)
      )

      root.style.setProperty('--theme-reveal-x', `${centerX}px`)
      root.style.setProperty('--theme-reveal-y', `${centerY}px`)
      root.style.setProperty('--theme-reveal-radius', `${Math.ceil(maxRadius)}px`)

      appEl.classList.remove('theme-transition-dark-expand', 'theme-transition-dark-collapse')
      // 触发重排，确保重复点击时动画能重新执行
      void appEl.offsetWidth

      this.themeTransitionActive = true
      appEl.classList.add(nextMode === 'dark' ? 'theme-transition-dark-expand' : 'theme-transition-dark-collapse')
      this.setThemeMode(nextMode)

      if (this.themeTransitionTimer) {
        clearTimeout(this.themeTransitionTimer)
      }
      this.themeTransitionTimer = window.setTimeout(() => {
        appEl.classList.remove('theme-transition-dark-expand', 'theme-transition-dark-collapse')
        this.themeTransitionActive = false
        this.themeTransitionTimer = null
      }, 620)
    },
    initializeTheme() {
      const storedTheme = localStorage.getItem(STORAGE_THEME_KEY)
      if (!storedTheme) {
        this.applyTheme(this.themePresets[0], false, false)
        return
      }

      try {
        const parsedTheme = JSON.parse(storedTheme)
        if (parsedTheme.id === 'custom' && parsedTheme.primary) {
          this.customThemeColor = parsedTheme.primary
          this.applyTheme(this.buildCustomTheme(parsedTheme.primary), false, false)
          return
        }

        const matchedTheme = this.themePresets.find((item) => item.id === parsedTheme.id)
        if (matchedTheme) {
          this.applyTheme(matchedTheme, false, false)
          return
        }
      } catch (error) {
        // 本地主题配置异常时回退默认主题
      }

      this.applyTheme(this.themePresets[0], false, false)
    },
    initializeThemeMode() {
      const storedMode = localStorage.getItem(STORAGE_THEME_MODE_KEY)
      this.setThemeMode(storedMode === 'dark' ? 'dark' : 'light', false, false)
    },
    initializeThemeDensity() {
      const storedDensity = localStorage.getItem(STORAGE_THEME_DENSITY_KEY)
      const validDensity = this.densityOptions.some((item) => item.value === storedDensity)
        ? storedDensity
        : 'comfortable'
      this.setThemeDensity(validDensity, false)
    },
    initializeLayoutMode() {
      const storedLayoutMode = localStorage.getItem(STORAGE_LAYOUT_MODE_KEY)
      const validLayoutMode = this.layoutOptions.some((item) => item.value === storedLayoutMode)
        ? storedLayoutMode
        : 'horizontal'
      this.setLayoutMode(validLayoutMode, false)
    },
    applyPresetTheme(theme) {
      this.customThemeColor = theme.primary
      this.applyTheme(theme)
    },
    applyCustomThemeColor() {
      this.applyTheme(this.buildCustomTheme(this.customThemeColor))
    },
    resetTheme() {
      this.themePopoverVisible = false
      this.customThemeColor = this.themePresets[0].primary
      this.setThemeMode('light')
      this.setThemeDensity('comfortable')
      this.setLayoutMode('horizontal')
      this.applyTheme(this.themePresets[0])
    },
    applyTheme(theme, persist = true, updateSurface = true) {
      const resolvedTheme = {
        id: theme.id,
        primary: theme.primary,
        pageBg: theme.pageBg,
        primaryDark: this.adjustColor(theme.primary, -18),
        primarySoft: this.mixWithWhite(theme.primary, 88),
        primaryBorder: this.mixWithWhite(theme.primary, 72),
        primaryShadow: this.hexToRgba(theme.primary, 0.22),
        headerBg: `linear-gradient(135deg, ${this.adjustColor(theme.primary, -8)} 0%, ${theme.primary} 100%)`,
        sidebarBg: `linear-gradient(180deg, ${this.adjustColor(theme.primary, -30)} 0%, ${this.adjustColor(theme.primary, -44)} 100%)`,
      }

      this.activeThemeId = resolvedTheme.id
      this.currentTheme = {
        id: resolvedTheme.id,
        primary: resolvedTheme.primary,
        pageBg: resolvedTheme.pageBg,
      }

      if (updateSurface) {
        this.applySurfaceTheme()
      }

      if (persist) {
        localStorage.setItem(
          STORAGE_THEME_KEY,
          JSON.stringify({
            id: resolvedTheme.id,
            primary: resolvedTheme.primary,
          })
        )
      }
    },
    applySurfaceTheme() {
      const root = document.documentElement
      const isDark = this.themeMode === 'dark'
      const baseTheme = this.currentTheme.primary ? this.currentTheme : this.themePresets[0]
      const surfaceMap = isDark
        ? {
            pageBg: '#0f172a',
            pageOverlay: 'radial-gradient(circle at top left, rgba(255,255,255,0.04), transparent 32%)',
            headerBg: 'rgba(15, 23, 42, 0.86)',
            headerBorder: 'rgba(148, 163, 184, 0.18)',
            headerText: '#f8fafc',
            headerSubtext: 'rgba(226, 232, 240, 0.72)',
            actionBg: 'rgba(30, 41, 59, 0.78)',
            actionBorder: 'rgba(148, 163, 184, 0.18)',
            actionHoverBg: 'rgba(51, 65, 85, 0.9)',
            sidebarBg: 'rgba(15, 23, 42, 0.78)',
            sidebarBorder: 'rgba(148, 163, 184, 0.16)',
            sidebarText: 'rgba(226, 232, 240, 0.72)',
            sidebarHover: 'rgba(71, 85, 105, 0.38)',
            mainPanelBg: 'rgba(15, 23, 42, 0.34)',
            cardBg: '#111827',
            cardBorder: 'rgba(148, 163, 184, 0.14)',
            tableHeadText: '#f8fafc',
            tableRowBg: '#111827',
            tableRowHover: 'rgba(30, 41, 59, 0.78)',
            textPrimary: '#f8fafc',
            textRegular: '#cbd5e1',
            textSecondary: '#94a3b8',
          }
        : {
            pageBg: baseTheme.pageBg,
            pageOverlay: `radial-gradient(circle at top left, ${this.hexToRgba(baseTheme.primary, 0.1)}, transparent 36%)`,
            headerBg: 'rgba(255, 255, 255, 0.88)',
            headerBorder: 'rgba(148, 163, 184, 0.18)',
            headerText: '#1e293b',
            headerSubtext: '#64748b',
            actionBg: 'rgba(255, 255, 255, 0.72)',
            actionBorder: 'rgba(203, 213, 225, 0.82)',
            actionHoverBg: 'rgba(255, 255, 255, 0.95)',
            sidebarBg: 'rgba(255, 255, 255, 0.8)',
            sidebarBorder: 'rgba(203, 213, 225, 0.74)',
            sidebarText: '#475569',
            sidebarHover: this.hexToRgba(baseTheme.primary, 0.1),
            mainPanelBg: 'rgba(255, 255, 255, 0.28)',
            cardBg: '#ffffff',
            cardBorder: '#e5e7eb',
            tableHeadText: '#ffffff',
            tableRowBg: '#ffffff',
            tableRowHover: this.hexToRgba(baseTheme.primary, 0.09),
            textPrimary: '#1f2937',
            textRegular: '#475569',
            textSecondary: '#94a3b8',
          }

      const densityMap = {
        compact: {
          headerHeight: '56px',
          sidebarWidth: '208px',
          menuItemHeight: '44px',
          mainPadding: '18px',
        },
        comfortable: {
          headerHeight: '64px',
          sidebarWidth: '228px',
          menuItemHeight: '48px',
          mainPadding: '24px',
        },
        spacious: {
          headerHeight: '72px',
          sidebarWidth: '244px',
          menuItemHeight: '54px',
          mainPadding: '30px',
        },
      }
      const densityConfig = densityMap[this.themeDensity] || densityMap.comfortable

      root.style.setProperty('--theme-primary', baseTheme.primary)
      root.style.setProperty('--theme-primary-dark', this.adjustColor(baseTheme.primary, -18))
      root.style.setProperty('--theme-primary-soft', this.mixWithWhite(baseTheme.primary, isDark ? 22 : 88))
      root.style.setProperty('--theme-primary-border', this.mixWithWhite(baseTheme.primary, isDark ? 16 : 72))
      root.style.setProperty('--theme-primary-shadow', this.hexToRgba(baseTheme.primary, isDark ? 0.3 : 0.22))
      root.style.setProperty('--theme-page-bg', surfaceMap.pageBg)
      root.style.setProperty('--theme-page-overlay', surfaceMap.pageOverlay)
      root.style.setProperty('--theme-header-bg', surfaceMap.headerBg)
      root.style.setProperty('--theme-header-border', surfaceMap.headerBorder)
      root.style.setProperty('--theme-header-text', surfaceMap.headerText)
      root.style.setProperty('--theme-header-subtext', surfaceMap.headerSubtext)
      root.style.setProperty('--theme-action-bg', surfaceMap.actionBg)
      root.style.setProperty('--theme-action-border', surfaceMap.actionBorder)
      root.style.setProperty('--theme-action-hover-bg', surfaceMap.actionHoverBg)
      root.style.setProperty('--theme-sidebar-bg', surfaceMap.sidebarBg)
      root.style.setProperty('--theme-sidebar-border', surfaceMap.sidebarBorder)
      root.style.setProperty('--theme-sidebar-text', surfaceMap.sidebarText)
      root.style.setProperty('--theme-sidebar-hover', surfaceMap.sidebarHover)
      root.style.setProperty('--theme-main-panel-bg', surfaceMap.mainPanelBg)
      root.style.setProperty('--theme-card-bg', surfaceMap.cardBg)
      root.style.setProperty('--theme-card-border', surfaceMap.cardBorder)
      root.style.setProperty('--theme-table-head-text', surfaceMap.tableHeadText)
      root.style.setProperty('--theme-table-row-bg', surfaceMap.tableRowBg)
      root.style.setProperty('--theme-table-row-hover', surfaceMap.tableRowHover)
      root.style.setProperty('--theme-text-primary', surfaceMap.textPrimary)
      root.style.setProperty('--theme-text-regular', surfaceMap.textRegular)
      root.style.setProperty('--theme-text-secondary', surfaceMap.textSecondary)
      root.style.setProperty('--layout-header-height', densityConfig.headerHeight)
      root.style.setProperty('--layout-sidebar-width', densityConfig.sidebarWidth)
      root.style.setProperty('--layout-menu-item-height', densityConfig.menuItemHeight)
      root.style.setProperty('--layout-main-padding', densityConfig.mainPadding)
    },
    setThemeMode(mode, persist = true, updateSurface = true) {
      this.themeMode = mode === 'dark' ? 'dark' : 'light'
      if (updateSurface) {
        this.applySurfaceTheme()
      }
      if (persist) {
        localStorage.setItem(STORAGE_THEME_MODE_KEY, this.themeMode)
      }
    },
    setThemeDensity(density, persist = true) {
      const validDensity = this.densityOptions.some((item) => item.value === density)
        ? density
        : 'comfortable'
      this.themeDensity = validDensity
      this.applySurfaceTheme()
      if (persist) {
        localStorage.setItem(STORAGE_THEME_DENSITY_KEY, this.themeDensity)
      }
    },
    setLayoutMode(mode, persist = true) {
      // 移动端不允许切换布局
      if (this.isMobile) {
        return
      }
      const validMode = this.layoutOptions.some((item) => item.value === mode)
        ? mode
        : 'horizontal'
      this.layoutMode = validMode
      this.isCollapsed = false
      if (persist) {
        localStorage.setItem(STORAGE_LAYOUT_MODE_KEY, this.layoutMode)
      }
    },
    buildCustomTheme(primary) {
      return {
        id: 'custom',
        primary,
        pageBg: this.mixWithWhite(primary, 94),
      }
    },
    handleUserCommand(command) {
      if (command === 'logout') {
        this.handleLogout()
      }
    },
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: 'logout-confirm-dialog',
      })
        .then(() => {
          this.logout()
          this.$router.push('/login')
        })
        .catch(() => {})
    },
    toggleMenu() {
      this.isCollapsed = !this.isCollapsed
    },
    checkMobile() {
      const isMobile = window.innerWidth < 768
      // 只有状态改变时才更新，避免频繁触发
      if (this.isMobile !== isMobile) {
        this.isMobile = isMobile
        // 移动端强制垂直布局
        if (isMobile) {
          this.layoutMode = 'vertical'
          this.isCollapsed = false // 移动端默认展开侧栏
        }
      }
    },
    hexToRgb(hex) {
      const normalized = hex.replace('#', '')
      const full = normalized.length === 3
        ? normalized
          .split('')
          .map((item) => `${item}${item}`)
          .join('')
        : normalized
      const value = parseInt(full, 16)
      return {
        r: (value >> 16) & 255,
        g: (value >> 8) & 255,
        b: value & 255,
      }
    },
    rgbToHex({ r, g, b }) {
      const toHex = (value) => value.toString(16).padStart(2, '0')
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`
    },
    adjustColor(hex, amount) {
      const { r, g, b } = this.hexToRgb(hex)
      const clamp = (value) => Math.max(0, Math.min(255, value + amount))
      return this.rgbToHex({
        r: clamp(r),
        g: clamp(g),
        b: clamp(b),
      })
    },
    mixWithWhite(hex, whitePercent) {
      const { r, g, b } = this.hexToRgb(hex)
      const ratio = whitePercent / 100
      const mix = (value) => Math.round(value + (255 - value) * ratio)
      return this.rgbToHex({
        r: mix(r),
        g: mix(g),
        b: mix(b),
      })
    },
    hexToRgba(hex, alpha) {
      const { r, g, b } = this.hexToRgb(hex)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
  },
}
</script>

<style>
:root {
  --theme-primary: #5b8def;
  --theme-primary-dark: #4a7de5;
  --theme-primary-soft: #eef4ff;
  --theme-primary-border: #bfd2fb;
  --theme-primary-shadow: rgba(91, 141, 239, 0.22);
  --theme-page-overlay: radial-gradient(circle at top left, rgba(91, 141, 239, 0.1), transparent 36%);
  --theme-header-bg: rgba(255, 255, 255, 0.88);
  --theme-header-border: rgba(148, 163, 184, 0.18);
  --theme-header-text: #1e293b;
  --theme-header-subtext: #64748b;
  --theme-action-bg: rgba(255, 255, 255, 0.72);
  --theme-action-border: rgba(203, 213, 225, 0.82);
  --theme-action-hover-bg: rgba(255, 255, 255, 0.95);
  --theme-sidebar-bg: rgba(255, 255, 255, 0.8);
  --theme-sidebar-border: rgba(203, 213, 225, 0.74);
  --theme-sidebar-text: #475569;
  --theme-sidebar-hover: rgba(91, 141, 239, 0.1);
  --theme-main-panel-bg: rgba(255, 255, 255, 0.28);
  --theme-card-bg: #ffffff;
  --theme-card-border: #e5e7eb;
  --theme-table-head-text: #ffffff;
  --theme-table-row-bg: #ffffff;
  --theme-table-row-hover: rgba(91, 141, 239, 0.09);
  --theme-page-bg: #f5f7fa;
  --theme-text-primary: #1f2937;
  --theme-text-regular: #475569;
  --theme-text-secondary: #94a3b8;
  --layout-header-height: 64px;
  --layout-sidebar-width: 228px;
  --layout-menu-item-height: 48px;
  --layout-main-padding: 24px;
}

/* 移动端侧边栏遮罩 */
.sidebar-mask {
  position: fixed;
  top: var(--layout-header-height);
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 900;
  animation: fadeIn 0.3s;
  backdrop-filter: blur(2px);
}

/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  height: 100%;
  font-family: 'PingFang SC', 'Helvetica Neue', Helvetica, 'Microsoft YaHei',
    Arial, sans-serif;
  background: var(--theme-page-bg);
  color: var(--theme-text-primary);
}

#app {
  height: 100%;
}

/* 全局动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* 过渡动画 */
.fade-slide-enter-active {
  animation: fadeIn 0.3s ease-out;
}

.fade-slide-leave-active {
  animation: fadeIn 0.2s ease-in reverse;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 主容器 */
.app-container {
  height: 100%;
  background: var(--theme-page-bg);
  position: relative;
}

.app-container::before {
  content: '';
  position: absolute;
  inset: 0;
  background: var(--theme-page-overlay);
  pointer-events: none;
}

.app-container::after {
  content: '';
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 2000;
  opacity: 0;
  background: #0f172a;
  clip-path: circle(0 at var(--theme-reveal-x, 100%) var(--theme-reveal-y, 0));
}

.app-container.theme-transition-dark-expand::after {
  opacity: 1;
  animation: themeDarkExpand 0.55s cubic-bezier(0.22, 0.84, 0.28, 1) forwards;
}

.app-container.theme-transition-dark-collapse::after {
  opacity: 1;
  animation: themeDarkCollapse 0.55s cubic-bezier(0.22, 0.84, 0.28, 1) forwards;
}

@keyframes themeDarkExpand {
  from {
    clip-path: circle(0 at var(--theme-reveal-x) var(--theme-reveal-y));
  }
  to {
    clip-path: circle(var(--theme-reveal-radius) at var(--theme-reveal-x) var(--theme-reveal-y));
  }
}

@keyframes themeDarkCollapse {
  from {
    clip-path: circle(var(--theme-reveal-radius) at var(--theme-reveal-x) var(--theme-reveal-y));
  }
  to {
    clip-path: circle(0 at var(--theme-reveal-x) var(--theme-reveal-y));
  }
}

.main-container {
  height: calc(100% - var(--layout-header-height));
  padding: 0 18px 18px;
  display: flex;
  width: 100%;
}

/* 顶部导航栏 */
.app-header {
  height: var(--layout-header-height) !important;
  background: var(--theme-header-bg);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 22px;
  margin: 12px 18px 10px;
  border: 1px solid var(--theme-header-border);
  border-radius: 20px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(18px);
  position: relative;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.menu-toggle-btn {
  margin-right: 4px;
  flex-shrink: 0;
}

.layout-horizontal .menu-toggle-btn {
  display: none;
}

.hamburger-icon {
  transition: all 0.3s ease;
}

.menu-toggle-btn:hover .hamburger-icon {
  transform: scale(1.1);
}

.logo-wrapper {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-primary-dark));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 10px 24px var(--theme-primary-shadow);
  font-size: 28px;
  color: #fff;
  flex-shrink: 0;
}

.logo-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px var(--theme-primary-shadow);
}

.header-nav-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 8px;
}

.header-nav-tabs::-webkit-scrollbar {
  height: 3px;
}

.header-nav-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.header-nav-tabs::-webkit-scrollbar-thumb {
  background: var(--theme-primary-soft);
  border-radius: 3px;
}

.nav-tab-item {
  padding: 8px 14px;
  border: none;
  background: transparent;
  color: var(--theme-header-text);
  font-size: 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.nav-tab-item i {
  font-size: 16px;
}

.nav-tab-item:hover {
  background: var(--theme-primary-soft);
  color: var(--theme-primary);
}

.nav-tab-item.active {
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-primary-dark));
  color: #fff;
  box-shadow: 0 4px 12px var(--theme-primary-shadow);
}

.app-brand {
  display: none;
}

.app-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--theme-header-text);
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.5px;
}

.app-title i {
  font-size: 22px;
  color: var(--theme-primary);
}

.app-subtitle {
  font-size: 12px;
  color: var(--theme-header-subtext);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.header-action-btn,
.account-trigger {
  height: 40px;
  border: 1px solid var(--theme-action-border);
  background: var(--theme-action-bg);
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--theme-header-text);
  cursor: pointer;
  transition: all 0.25s ease;
  backdrop-filter: blur(8px);
}

.header-action-btn:hover,
.account-trigger:hover {
  background: var(--theme-action-hover-bg);
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
}

.header-action-btn {
  width: 40px;
  padding: 0 !important;
  font-size: 18px !important;
}

.theme-trigger {
  position: relative;
}

.mode-switch-btn {
  overflow: hidden;
}

.mode-switch-btn i {
  font-size: 17px;
  transition: transform 0.3s ease, color 0.3s ease;
}

.mode-switch-btn.is-dark i {
  color: #facc15;
}

.mode-switch-btn:not(.is-dark) i {
  color: #1e293b;
}

.mode-switch-btn.is-animating i {
  transform: rotate(18deg) scale(1.08);
}

.theme-trigger__dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid #fff;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
}

.role-worker {
  background: linear-gradient(135deg, #36cfc9 0%, #13c2c2 100%);
}

.role-reviewer {
  background: linear-gradient(135deg, #fa8c16 0%, #fa541c 100%);
}

.role-superadmin {
  background: linear-gradient(135deg, #9254de 0%, #722ed1 100%);
}

.role-default {
  background: linear-gradient(135deg, #69b1ff 0%, #409eff 100%);
}

.account-trigger {
  min-width: 84px;
  padding: 2px 10px 2px 2px;
  border-radius: 999px;
  gap: 8px;
  justify-content: space-between;
}

.account-trigger__avatar {
  width: 34px;
  height: 34px;
  font-size: 17px;
}

.account-trigger__meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.account-trigger__role {
  font-size: 12px;
  font-weight: 600;
  color: var(--theme-header-text);
  line-height: 1;
}

.account-arrow {
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.account-trigger > .account-arrow {
  display: none;
}

/* 侧边导航 */
.app-aside {
  background: var(--theme-sidebar-bg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--theme-sidebar-border);
  border-right: 1px solid var(--theme-sidebar-border);
  overflow: hidden;
  /* border-radius: 24px; */
  margin-right: 18px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(18px);
}

.layout-horizontal .app-aside.horizontal-aside {
  width: var(--layout-sidebar-width) !important;
  flex: 0 0 var(--layout-sidebar-width);
  height: 100%;
  border-radius: 20px;
}

.layout-vertical .app-aside:not(.collapsed) {
  width: var(--layout-sidebar-width) !important;
}

.layout-vertical .app-aside.collapsed {
  width: 72px !important;
}

.layout-vertical .app-aside {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  margin: 0;
  border-left: none;
  border-right: none;
  box-shadow: none;
  z-index: 920;
}

.layout-vertical .aside-brand {
  margin: 3px 0 3px;
  border-radius: 0;
}

.layout-vertical .aside-menu {
  padding-top: 6px;
}

.layout-horizontal .aside-menu {
  padding-top: 6px;
}

.aside-empty-state {
  padding: 14px 18px;
  color: var(--theme-text-secondary);
  font-size: 13px;
}

.aside-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 12px 4px;
  padding: 10px;
  border-radius: 14px;
}

.aside-brand.collapsed {
  justify-content: center;
  padding: 10px 6px;
}

.aside-brand__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-primary-dark));
  color: #fff;
  font-size: 20px;
  box-shadow: 0 8px 16px var(--theme-primary-shadow);
}

.aside-brand__text {
  min-width: 0;
}

.aside-brand__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--theme-text-primary);
  white-space: nowrap;
}

.aside-brand__subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: var(--theme-text-secondary);
  white-space: nowrap;
}

.aside-menu {
  background: transparent !important;
  border-right: none !important;
  padding: 10px 6px;
}

.aside-menu .el-menu-item {
  height: var(--layout-menu-item-height);
  line-height: var(--layout-menu-item-height);
  margin: 6px 0;
  border-radius: 12px;
  color: var(--theme-sidebar-text);
  transition: all 0.25s ease;
  overflow: hidden;
  font-weight: 500;
}

.aside-menu .el-menu-item i {
  color: var(--theme-sidebar-text);
  font-size: 18px;
  transition: all 0.25s ease;
}

.aside-menu .el-menu-item:hover {
  background: var(--theme-sidebar-hover);
  color: var(--theme-primary);
  transform: translateX(2px);
}

.aside-menu .el-menu-item:hover i {
  color: var(--theme-primary);
}

.aside-menu .el-menu-item.is-active {
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-primary-dark));
  color: #fff;
  box-shadow: 0 10px 24px var(--theme-primary-shadow);
}

.aside-menu .el-menu-item.is-active i {
  color: #fff;
}

.aside-menu .el-submenu__title {
  height: var(--layout-menu-item-height);
  line-height: var(--layout-menu-item-height);
  margin: 6px 0;
  border-radius: 12px;
  color: var(--theme-sidebar-text);
  font-weight: 500;
  transition: all 0.25s ease;
}

.aside-menu .el-submenu__title:hover {
  background: var(--theme-sidebar-hover);
  color: var(--theme-primary);
}

.aside-menu .el-submenu__title i {
  color: inherit;
}

.menu-item-animate {
  animation: slideInLeft 0.3s ease-out;
}

/* 主内容区 */
.app-main {
  background: var(--theme-main-panel-bg);
  border: 1px solid var(--theme-sidebar-border);
  border-radius: 26px;
  padding: var(--layout-main-padding);
  overflow-y: auto;
  position: relative;
  backdrop-filter: blur(16px);
  flex: 1;
  width: 100%;
}

.layout-vertical .app-main {
  width: auto;
  margin-left: var(--layout-sidebar-width);
}

.layout-horizontal .app-main {
  min-width: 0;
  width: auto;
}

.layout-vertical.menu-collapsed .app-main {
  margin-left: 72px;
}

.layout-vertical .app-header {
  margin: 0 0 0 var(--layout-sidebar-width);
}

.layout-vertical.menu-collapsed .app-header {
  margin: 0 0 0 72px;
}

.layout-vertical .app-header,
.layout-vertical .app-main {
  border: none;
  border-radius: 0;
  box-shadow: none;
  backdrop-filter: none;
}

.layout-vertical .app-header {
  background: transparent;
  border-left: 1px solid var(--theme-sidebar-border);
  border-bottom: 1px solid var(--theme-sidebar-border);
}

.layout-vertical .app-main {
  background: transparent;
  border-left: 1px solid var(--theme-sidebar-border);
}

.layout-vertical .main-container {
  padding: 0;
}

.app-main::before {
  display: none;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .layout-vertical .app-header,
  .layout-vertical.menu-collapsed .app-header {
    margin: 10px 12px 8px;
    border-left: none;
    border-bottom: none;
  }

  .layout-vertical .app-main,
  .layout-vertical.menu-collapsed .app-main {
    margin-left: 0;
    border-left: none;
  }

  .layout-vertical .app-aside {
    top: calc(var(--layout-header-height) + 14px);
    left: 0;
    bottom: 0;
    margin: 0 12px 12px;
    border-left: 1px solid var(--theme-sidebar-border);
    border-radius: 24px;
  }

  /* 移动端侧栏收缩时完全隐藏 */
  .layout-vertical .app-aside.collapsed {
    display: none !important;
  }

  .app-header {
    margin: 10px 12px 8px;
    padding: 0 14px;
  }

  .app-title {
    font-size: 16px;
  }

  .app-subtitle {
    display: none;
  }

  .header-right {
    gap: 8px;
  }

  .app-main {
    padding: 16px;
    border-radius: 20px;
  }
}

/* Element UI 样式覆盖 */
.el-button--primary {
  background: var(--theme-primary);
  border: none;
  transition: all 0.2s ease;
}

.el-button--primary:hover {
  background: var(--theme-primary-dark);
  box-shadow: 0 8px 20px var(--theme-primary-shadow);
}

.el-button--success {
  background: #52c41a;
  border: none;
}

.el-button--success:hover {
  background: #47b016;
}

.el-button--warning {
  background: #faad14;
  border: none;
}

.el-card {
  border-radius: 12px;
  border: 1px solid var(--theme-card-border);
  background: var(--theme-card-bg);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  transition: all 0.2s ease;
}

.el-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.el-table {
  border-radius: 12px;
  overflow: hidden;
  background: var(--theme-card-bg);
}

.el-table,
.el-table tr,
.el-table td,
.el-table th,
.el-table__expanded-cell,
.el-table__fixed,
.el-table__fixed-right,
.el-table__fixed-body-wrapper,
.el-table__body-wrapper {
  background-color: var(--theme-table-row-bg);
}

.el-table td,
.el-table th.is-leaf {
  border-bottom: 1px solid var(--theme-card-border);
}

.el-table--enable-row-hover .el-table__body tr:hover > td {
  background-color: var(--theme-table-row-hover);
}

.el-table th {
  background: var(--theme-primary) !important;
  color: var(--theme-table-head-text) !important;
  font-weight: 600;
}

.el-pagination.is-background .el-pager li:not(.disabled).active {
  background: var(--theme-primary);
}

.el-tabs__item.is-active {
  color: var(--theme-primary);
}

.el-tabs__active-bar {
  background: var(--theme-primary);
}

.el-dialog {
  border-radius: 12px;
  overflow: hidden;
  background: var(--theme-card-bg);
}

.el-dialog__header {
  background: var(--theme-primary);
  padding: 16px 20px;
}

.el-dialog__title {
  color: #fff;
  font-weight: 600;
}

.el-dialog__headerbtn .el-dialog__close {
  color: #fff;
}

/* 退出确认对话框 */
.logout-confirm-dialog {
  position: fixed !important;
  top: 20% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 2001 !important;
}

.logout-confirm-dialog .el-dialog__body {
  font-size: 14px;
  line-height: 1.6;
  padding: 16px 20px !important;
}

@media screen and (max-width: 768px) {
  .logout-confirm-dialog {
    width: calc(100vw - 40px) !important;
    max-width: 300px !important;
  }

  .logout-confirm-dialog .el-dialog__header {
    padding: 12px 16px !important;
  }

  .logout-confirm-dialog .el-dialog__body {
    padding: 12px 16px !important;
    font-size: 13px;
  }

  .logout-confirm-dialog .el-dialog__footer {
    padding: 12px 16px !important;
  }

  .logout-confirm-dialog .el-button {
    height: 36px !important;
    padding: 0 16px !important;
    font-size: 13px;
  }
}

.el-input__inner:focus {
  border-color: var(--theme-primary);
  box-shadow: 0 0 0 2px var(--theme-primary-shadow);
}

.el-tag--success {
  background: #52c41a;
  border: none;
  color: #fff;
}

.el-tag--warning {
  background: #faad14;
  border: none;
  color: #fff;
}

.el-tag--info {
  background: var(--theme-primary);
  border: none;
  color: #fff;
}

.el-tag--danger {
  background: #ff4d4f;
  border: none;
  color: #fff;
}

.el-badge__content {
  background: #ff4d4f;
  border: none;
}

.theme-panel {
  padding: 4px;
}

.theme-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.theme-panel__title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.theme-panel__desc {
  margin-top: 4px;
  font-size: 12px;
  color: #8c8c8c;
}

.theme-preview-chip {
  width: 28px;
  height: 28px;
  border-radius: 10px;
  box-shadow: 0 6px 16px var(--theme-primary-shadow);
}

.theme-section + .theme-section {
  margin-top: 18px;
}

.theme-section__label {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
}

.theme-preset-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.theme-preset {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.theme-preset:hover,
.theme-preset.active {
  border-color: var(--theme-primary-border);
  background: var(--theme-primary-soft);
  box-shadow: 0 8px 20px var(--theme-primary-shadow);
}

.theme-preset__swatch {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.8);
}

.theme-preset__name {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.theme-color-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 14px;
  background: #fafbfd;
}

.theme-color-input {
  width: 44px;
  height: 44px;
  border: none;
  padding: 0;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
}

.theme-color-input::-webkit-color-swatch-wrapper {
  padding: 0;
}

.theme-color-input::-webkit-color-swatch {
  border: none;
  border-radius: 12px;
}

.theme-color-meta {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.theme-color-value {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.5px;
}

.theme-reset-btn {
  color: var(--theme-primary) !important;
  padding: 0 !important;
}

.theme-option-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.density-list {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.layout-list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.theme-option {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 12px;
  height: 40px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  color: #374151;
  transition: all 0.2s ease;
}

.theme-option:hover,
.theme-option.active {
  border-color: var(--theme-primary-border);
  background: var(--theme-primary-soft);
  color: var(--theme-primary);
}

.density-option {
  font-size: 12px;
  font-weight: 600;
}

.layout-option {
  font-size: 12px;
  font-weight: 600;
}

.account-dropdown-menu {
  padding: 8px !important;
  border-radius: 16px !important;
  min-width: 220px;
  border: 1px solid var(--theme-card-border) !important;
  background: var(--theme-card-bg) !important;
}

.account-summary-item {
  padding: 0 !important;
  cursor: default !important;
}

.account-summary-item.is-disabled {
  color: inherit !important;
}

.account-summary-item.is-disabled:hover,
.account-summary-item.is-disabled:focus {
  background: transparent !important;
}

.account-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 14px;
}

.account-summary__avatar {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 20px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.12);
}

.account-summary__content {
  min-width: 0;
}

.account-summary__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--theme-text-primary);
}

.account-summary__role {
  margin-top: 4px;
  font-size: 12px;
  color: var(--theme-text-secondary);
}

.logout-menu-item {
  border-radius: 12px;
  transition: all 0.2s ease;
}

.logout-menu-item i {
  margin-right: 6px;
}

.logout-menu-item:hover {
  color: #ff4d4f !important;
  background: #fff1f0 !important;
}
</style>
